import React from "react";
import "../styles/PrivacyPolicy.css";

export default function PrivacyPolicy() {
  return (
    <div className="privacy-policy-container">
      <div className="privacy-content">
        <h1 className="privacy-title">Privacy Policy - MediCoin ICO</h1>
        
        <div className="policy-meta">
          <p><strong>Last Updated:</strong> November 8, 2025</p>
          <p><strong>Effective Date:</strong> November 8, 2025</p>
        </div>

        <section className="policy-section">
          <h2>1. Introduction</h2>
          <p>
            MediCoin, managed and operated by MediCureOn LLC ("we," "us," "our," or "Company"), respects your privacy and is committed to protecting it. 
            This Privacy Policy explains how we collect, use, disclose, and otherwise process personal information 
            in connection with the MediCoin ICO (Initial Coin Offering), our website, and related services.
          </p>
          <p>
            <strong>What is MediCoin?</strong> MediCoin (MCN) is a <strong>utility token</strong> designed to facilitate transactions, payments, and rewards 
            within the MediCureOn Wellness Platform. Unlike speculative or meme coins, MediCoin has real-world utility: 
            it powers healthcare services, enables decentralized medical data sharing, facilitates peer-to-peer telehealth payments, 
            and provides access to premium wellness features on the MediCureOn ecosystem. MediCoin is not an independent project 
            - it is directly managed by MediCureOn LLC to power healthcare services, payments, and rewards within our comprehensive wellness ecosystem.
          </p>
          <p>
            By accessing and using our services, you agree to this Privacy Policy.
          </p>
        </section>

        <section className="policy-section">
          <h2>2. Important Legal Disclaimers</h2>
          <div className="bg-yellow-500/10 border-l-4 border-yellow-500 pl-4 py-3 mb-4 rounded">
            <p className="text-yellow-100 font-semibold mb-2">⚠️ IMPORTANT NOTICES:</p>
            <ul className="text-yellow-50 text-sm space-y-2">
              <li>
                <strong>Not Securities:</strong> MediCoin (MCN) tokens are not securities. Purchases during the ICO phase are voluntary contributions to access ecosystem utility features on MediCureOn.
              </li>
              <li>
                <strong>Final and Non-Refundable:</strong> All purchases are final and non-refundable. Please read carefully before participating.
              </li>
              <li>
                <strong>Regulatory Risk:</strong> Cryptocurrency and token regulations are evolving. Participants assume all legal and financial risks.
              </li>
            </ul>
          </div>
        </section>

        <section className="policy-section">
          <h2>3. Scope</h2>
          <p>This Privacy Policy applies to:</p>
          <ul>
            <li>Personal information collected on or through our website and ICO platform</li>
            <li>Information provided during presale registration and token purchases</li>
            <li>Information collected through wallet connections and blockchain interactions</li>
            <li>Communication with our team and support services</li>
          </ul>
        </section>

        <section className="policy-section">
          <h2>4. Information We Collect</h2>
          
          <h3>A. Minimal Information Collection</h3>
          <p>
            MediCoin ICO operates on a non-custodial model. We collect minimal information:
          </p>
          <ul>
            <li><strong>Wallet Address:</strong> Your public blockchain wallet address (required to send purchased tokens)</li>
            <li><strong>Email Address:</strong> Optional, for presale updates and token claim notifications</li>
            <li><strong>Browser/Device Data:</strong> Standard analytics (IP, browser type, pages visited)</li>
          </ul>

          <h3>B. Information We Do NOT Collect</h3>
          <p>
            <strong>Unlike traditional fintech platforms, MediCoin operates as a non-custodial ICO.</strong> We explicitly do NOT collect or require:
          </p>
          <ul>
            <li><strong>No KYC/AML data:</strong> Identification documents, proof of address, or regulatory verification</li>
            <li><strong>No personal identity information:</strong> Name, legal name, date of birth, residential address</li>
            <li><strong>No payment method details:</strong> Credit cards, bank accounts, or payment processor information (direct peer-to-peer blockchain transactions only)</li>
            <li><strong>No health or medical information:</strong> Medical records, health status, or personal health data</li>
            <li><strong>No compliance records:</strong> Tax IDs, employment status, or financial history</li>
          </ul>
          <p>
            <strong>Why?</strong> Because we never hold your funds. You send crypto directly to the smart contract and receive tokens immediately. This decentralized model eliminates the need for traditional personal data collection and KYC procedures.
          </p>

          <h3>C. Blockchain Data (Public)</h3>
          <ul>
            <li>All token purchase transactions are recorded on the Ethereum blockchain</li>
            <li>This data is publicly visible and immutable</li>
            <li>We do not control or store this data privately</li>
          </ul>
        </section>

        <section className="policy-section">
          <h2>5. How We Use Your Information</h2>
          <p>We use collected information for:</p>
          <ul>
            <li>Sending purchased tokens to your wallet address</li>
            <li>Delivering presale updates and token claim notifications (email)</li>
            <li>Providing customer support and responding to inquiries</li>
            <li>Improving our platform and user experience through analytics</li>
            <li>Preventing fraud and maintaining security</li>
            <li>Resolving disputes and handling technical issues</li>
            <li>Complying with applicable laws (if required by legal authorities)</li>
          </ul>
        </section>

        <section className="policy-section">
          <h2>6. Data Sharing & Disclosure</h2>
          
          <h3>We Share Data With:</h3>
          <ul>
            <li><strong>Blockchain Networks:</strong> Public on-chain transaction data (Ethereum blockchain - immutable)</li>
            <li><strong>Analytics Services:</strong> Google Analytics (aggregated, anonymized usage data)</li>
            <li><strong>Legal Authorities:</strong> Only when required by valid law, court order, or regulatory request</li>
          </ul>

          <h3>We Do NOT Share Data With:</h3>
          <ul>
            <li>Payment processors (no payment collection)</li>
            <li>KYC/AML service providers (no identity verification)</li>
            <li>Marketing or advertising companies</li>
            <li>Third parties for any purpose without legal requirement</li>
          </ul>

          <h3>Important Note:</h3>
          <p>
            Since we operate a non-custodial token presale, transactions are peer-to-peer. We do not hold, 
            process, or control your funds. All transactions are final and recorded on the Ethereum blockchain.
          </p>
        </section>

        <section className="policy-section">
          <h2>7. Data Security</h2>
          <p>
            We implement industry-leading security measures to protect your information:
          </p>
          <ul>
            <li><strong>Encryption:</strong> AES-256 encryption for data in transit and at rest</li>
            <li><strong>Access Controls:</strong> Role-based access, principle of least privilege</li>
            <li><strong>Multi-Factor Authentication (MFA):</strong> Required for sensitive account actions</li>
            <li><strong>Secure Infrastructure:</strong> Microsoft Azure with HIPAA/SOC 2 compliance</li>
            <li><strong>Regular Audits:</strong> Third-party security assessments and penetration testing</li>
            <li><strong>Incident Response:</strong> Monitored 24/7 with rapid breach notification procedures</li>
          </ul>
        </section>

        <section className="policy-section">
          <h2>8. Your Rights & Choices</h2>
          <p>
            Since we collect minimal personal information, your primary rights relate to that data:
          </p>
          <ul>
            <li><strong>Right to Access:</strong> Request information about what data we hold about you</li>
            <li><strong>Right to Deletion:</strong> Request deletion of your email address from our mailing list</li>
            <li><strong>Right to Opt-Out:</strong> Unsubscribe from presale updates and notifications at any time</li>
            <li><strong>Right to Object:</strong> Object to analytics and cookies</li>
          </ul>
          <p>
            <strong>Note:</strong> Blockchain transaction data is permanent and cannot be deleted (by design).
          </p>
          <p>
            To exercise these rights or inquire about your data, contact us at: <strong>privacy@medicureon.com</strong>
          </p>
        </section>

        <section className="policy-section">
          <h2>9. Data Retention</h2>
          <ul>
            <li><strong>Email Addresses:</strong> Retained for presale updates; can be deleted upon request</li>
            <li><strong>Wallet Addresses:</strong> Retained for token delivery and verification purposes</li>
            <li><strong>Analytics Data:</strong> Aggregated and anonymized, retained for service improvement</li>
            <li><strong>Blockchain Data:</strong> Public on-chain transactions cannot be deleted (immutable by design)</li>
            <li><strong>Upon Request:</strong> Personal data (email) securely deleted within 30 days</li>
          </ul>
        </section>

        <section className="policy-section">
          <h2>10. Cookies & Tracking</h2>
          <p>
            We use cookies and similar tracking technologies to:
          </p>
          <ul>
            <li>Remember your preferences and login status</li>
            <li>Analyze site usage through Google Analytics</li>
            <li>Prevent fraud and maintain security</li>
          </ul>
          <p>
            You can control cookies through your browser settings. Disabling cookies may affect site functionality.
          </p>
        </section>

        <section className="policy-section">
          <h2>11. Compliance & Legal Basis</h2>
          
          <h3>GDPR Compliance (EU Users):</h3>
          <ul>
            <li>Legal basis for processing: Legitimate interest (providing ICO service)</li>
            <li>Minimal data collection - only what's necessary for token delivery</li>
            <li>Right to lodge complaints with your local data protection authority</li>
          </ul>

          <h3>CCPA Compliance (California Users):</h3>
          <ul>
            <li>Right to know what personal information is collected</li>
            <li>Right to delete your email address from our records</li>
            <li>Right to opt-out of future communications</li>
            <li>We do not sell personal data</li>
          </ul>

          <h3>Blockchain Transparency:</h3>
          <ul>
            <li>All token transactions are recorded on Ethereum blockchain (public ledger)</li>
            <li>No regulatory KYC/AML collection (non-custodial model)</li>
            <li>Investors are responsible for compliance with local laws</li>
          </ul>
        </section>

        <section className="policy-section">
          <h2>12. Third-Party Links & Services</h2>
          <p>
            Our website may contain links to third-party services (Etherscan, wallet providers, exchanges). 
            We are not responsible for their privacy practices. We recommend reviewing their privacy policies before sharing information.
          </p>
        </section>

        <section className="policy-section">
          <h2>13. Policy Updates</h2>
          <p>
            We may update this Privacy Policy as our services evolve or regulations change. We will:
          </p>
          <ul>
            <li>Post updated versions with a new "Last Updated" date</li>
            <li>Notify users of material changes via email</li>
            <li>Request explicit consent for substantial changes affecting your rights</li>
          </ul>
        </section>

        <section className="policy-section">
          <h2>14. Contact Information</h2>
          <p>
            For privacy questions, concerns, or to exercise your rights:
          </p>
          <ul className="contact-info">
            <li><strong>Email:</strong> privacy@medicureon.com</li>
            <li><strong>Website:</strong> www.medicureon.com</li>
            <li><strong>Support:</strong> support@medicureon.com</li>
          </ul>
          <p>
            We will respond to privacy requests within 30 days or as required by applicable law.
          </p>
        </section>

        <section className="policy-section">
          <h2>15. User Acknowledgment</h2>
          <p>
            By using our ICO platform and services, you acknowledge that you have read and understood 
            this Privacy Policy and consent to:
          </p>
          <ul>
            <li>Collection and processing of your personal information</li>
            <li>Use of data for the purposes described herein</li>
            <li>Sharing with trusted partners under confidentiality agreements</li>
            <li>International data transfers with appropriate legal protections</li>
          </ul>
        </section>

        <div className="policy-footer">
          <p><strong>Last Updated:</strong> November 8, 2025</p>
          <p><strong>MediCoin | Managed by MediCureOn LLC | Powering Healthcare with DeFi</strong></p>
        </div>
      </div>
    </div>
  );
}
